import {
  Add,
  DeleteOutlineRounded,
  EditNoteOutlined,
  EditOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import * as UserActions from "../../../../redux/users/users.actions";
import * as userReducer from "../../../../redux/users/users.reducers";
import CustomTooltip from "../../../../utils/CustomTooltip";
import DeleteItemDialog from "../../../../utils/common/delete-item-dialog/DeleteItemDialog";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import { primary } from "../../../../themes/theme";

declare const window: Window &
  typeof globalThis & {
    cloudinary: {
      createUploadWidget: (p: {}, p1: (error: any, result: any) => void) => any;
    };
  };

export function AddressDisplayText(props: any) {
  const textStyle = {
    fontWeight: "500",
    fontFamily: "Ubuntu",
  };

  return (
    <Grid container spacing={1} sx={{ mb: 1 }}>
      <Grid item xs={3}>
        <Typography sx={{ ...textStyle }}>{props.primary}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Typography sx={{ ...textStyle }}>
          {" "}
          -&nbsp; {props.secondary}
        </Typography>
      </Grid>
    </Grid>
  );
}

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useAppDispatch();
  const cloudinaryRef = useRef<any>();
  const widgetRef = useRef<any>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [addrIdToDelete, setAddrIdToDelete] = useState<string | undefined>("");

  //Get Data From Redux
  const userState: userReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[userReducer.userFeatureKey];
    }
  );

  const { user, loading, address } = userState;

  useEffect(() => {
    dispatch(UserActions.getAddressAction());
  }, []);

  const handleDeleteAddress = (addressId: string | undefined) => {
    setDeleteDialogOpen(true);
    setAddrIdToDelete(addressId);
  };

  const handleDeleteConfirm = () => {
    if (addrIdToDelete) {
      dispatch(UserActions.deleteAddressAction({ addressId: addrIdToDelete }));
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  useEffect(() => {
    cloudinaryRef.current = window.cloudinary;
    widgetRef.current = cloudinaryRef?.current?.createUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
        uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
      },
      (error: any, result: { event: string; info: { secure_url: any } }) => {
        if (!error && result && result.event === "success") {
          if (result.info.secure_url) {
            const imageUrl = result.info.secure_url;
            dispatch(
              UserActions.updateProfilePicAction({ imageUrl: imageUrl })
            );
            // alert(result.info.secure_url);
          }
        }
      }
    );
  }, []);

  const handleEditImage = () => {
    widgetRef.current.open();
  };

  return (
    <>
      <Box sx={{ pb: 2, mb: 2 }}>
        {loading && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="User Profile"
          description="Manage your profiles, preferences, and settings with ease and accuracy."
        />
        <Grid
          container
          sx={{ gap: "10px", pl: { xs: 1, md: 9, lg: 10 }, pr: 1 }}
        >
          {user && Object.keys(user).length > 0 && (
            <>
              <Grid
                component={Paper}
                elevation={0}
                item
                xs={12}
                sm={12}
                md={3}
                sx={{ borderRadius: "10px", height: "100%", pb: 1 }}
              >
                <Card elevation={0}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      height: "100%",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Ubuntu",
                        pt: 1,
                        fontSize: "18px",
                        letterSpacing: "1px",
                        width: "100%",
                        ml: 2,
                        fontWeight: "bold",
                      }}
                    >
                      | Profile Picture
                    </Typography>
                    <CustomTooltip title="Change Profile Pic" placement="top">
                      <IconButton
                        size="small"
                        sx={{
                          background: "transparent",
                          color: "green",
                          borderRadius: "7px",
                          width: "25px",
                          height: "25px",
                          mt: 1,
                          mr: 2,
                          border: "2px solid green",
                          "&:hover": {
                            background: "green",
                            color: "white",
                          },
                        }}
                        onClick={handleEditImage}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 0,
                      color: primary[900],
                      border: "1px dashed #f1f2f3",
                    }}
                  />
                  <CardContent>
                    <img
                      // src="https://images.ctfassets.net/lh3zuq09vnm2/yBDals8aU8RWtb0xLnPkI/19b391bda8f43e16e64d40b55561e5cd/How_tracking_user_behavior_on_your_website_can_improve_customer_experience.png"
                      src={user?.imageUrl}
                      alt=""
                      style={{ width: "100%", borderRadius: "10px" }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                component={Paper}
                elevation={0}
                item
                xs={12}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                sx={{ borderRadius: "10px", height: "100%", pb: 2 }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} lg={5} xl={5}>
                    <Card elevation={0} sx={{ width: "100%", p: 0, m: 0 }}>
                      <Typography
                        sx={{
                          // textAlign: "center",
                          // background: "#000048",
                          // color: "white",
                          fontFamily: "Ubuntu",
                          pt: 1,
                          fontSize: "18px",
                          letterSpacing: "1px",
                          width: "100%",
                          ml: 2,
                          fontWeight: "bold",
                        }}
                      >
                        | Your Info
                      </Typography>
                      <Divider
                        sx={{
                          mt: 1,
                          mb: 0,
                          color: primary[900],
                          border: "1px dashed #f1f2f3",
                        }}
                      />
                      <CardContent>
                        <AddressDisplayText
                          primary={"Name"}
                          secondary={user.username}
                        />
                        <AddressDisplayText
                          primary={"Email"}
                          secondary={user?.email}
                        />
                        <AddressDisplayText
                          primary={"Admin"}
                          secondary={user?.isAdmin === false ? "No" : "Yes"}
                        />
                        <AddressDisplayText
                          primary={"Super Admin"}
                          secondary={
                            user?.isSuperAdmin === false ? "No" : "Yes"
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    lg={1}
                    xl={1}
                    sx={{
                      display: { xs: "none", lg: "flex" },
                      justifyContent: "center",
                      width: "100%",
                      pr: "auto",
                    }}
                  >
                    <Divider
                      orientation="vertical"
                      variant="middle"
                      sx={{ color: primary[900], border: "1px dashed #f1f2f3" }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    lg={6}
                    xl={6}
                    sx={{ width: "100%", mr: "auto", pr: "auto" }}
                  >
                    <Card elevation={0} sx={{ width: "100%", p: 0, m: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          height: "100%",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        {address && Object.keys(address).length > 0 ? (
                          <>
                            <Typography
                              sx={{
                                // textAlign: "center",
                                // background: "#000048",
                                // color: "white",
                                fontFamily: "Ubuntu",
                                pt: 1,
                                fontSize: "18px",
                                letterSpacing: "1px",
                                ml: 2,
                                fontWeight: "bold",
                              }}
                            >
                              | Shipping Address
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                pt: 1,
                                pr: 2,
                                gap: 1,
                              }}
                            >
                              <CustomTooltip title="Edit" placement="top">
                                <IconButton
                                  size="small"
                                  sx={{
                                    background: "transparent",
                                    color: "green",
                                    borderRadius: "7px",
                                    width: "25px",
                                    height: "25px",
                                    border: "2px solid green",
                                    "&:hover": {
                                      background: "green",
                                      color: "white",
                                    },
                                  }}
                                  onClick={() =>
                                    navigate(
                                      `/users/edit-shipping-address/${address._id}`
                                    )
                                  }
                                >
                                  <EditNoteOutlined fontSize="small" />
                                </IconButton>
                              </CustomTooltip>
                              <CustomTooltip title="Delete" placement="top">
                                <IconButton
                                  onClick={() =>
                                    handleDeleteAddress(address._id)
                                  }
                                  size="small"
                                  sx={{
                                    background: "transparent",
                                    color: "orangered",
                                    borderRadius: "7px",
                                    width: "25px",
                                    height: "25px",
                                    border: "2px solid orangered",
                                    "&:hover": {
                                      background: "orangered",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <DeleteOutlineRounded fontSize="small" />
                                </IconButton>
                              </CustomTooltip>
                            </Box>
                          </>
                        ) : (
                          <>
                            <Typography
                              sx={{
                                // textAlign: "center",
                                // background: "#000048",
                                // color: "white",
                                fontFamily: "Ubuntu",
                                pt: 1,
                                fontSize: "18px",
                                letterSpacing: "1px",
                                width: "100%",
                                ml: 2,
                                fontWeight: "bold",
                              }}
                            >
                              Add Shipping Address
                            </Typography>
                            <CustomTooltip title="Add Address" placement="top">
                              <IconButton
                                size="small"
                                sx={{
                                  background: "transparent",
                                  color: "green",
                                  borderRadius: "7px",
                                  width: "25px",
                                  height: "25px",
                                  border: "2px solid green",
                                  mr: 2,
                                  mt: 1,
                                  "&:hover": {
                                    background: "green",
                                    color: "white",
                                  },
                                }}
                                onClick={() =>
                                  navigate("/users/add-shipping-address/")
                                }
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </CustomTooltip>
                          </>
                        )}
                      </Box>
                      <Divider
                        sx={{
                          mt: 1,
                          mb: 0,
                          color: primary[900],
                          border: "1px dashed #f1f2f3",
                        }}
                      />
                      <CardContent>
                        {address && Object.keys(address).length > 0 && (
                          <React.Fragment key={address._id}>
                            <AddressDisplayText
                              primary={"Mobile"}
                              secondary={address?.mobile}
                            />
                            <AddressDisplayText
                              primary={"Flat"}
                              secondary={address?.flat}
                            />
                            <AddressDisplayText
                              primary={"Street"}
                              secondary={address?.street}
                            />
                            <AddressDisplayText
                              primary={"Landmark"}
                              secondary={address?.landmark}
                            />
                            <AddressDisplayText
                              primary={"City"}
                              secondary={address?.city}
                            />
                            <AddressDisplayText
                              primary={"State"}
                              secondary={address?.state}
                            />
                            <AddressDisplayText
                              primary={"Country"}
                              secondary={address?.country}
                            />
                            <AddressDisplayText
                              primary={"Pin Code"}
                              secondary={address?.pinCode}
                            />
                          </React.Fragment>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Address Delete Dialog  */}
      <DeleteItemDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Address Delete?"
        contentText="Are you sure you want to delete the address"
      />
    </>
  );
};

export default UserProfile;
